import { FormProvider, useForm } from 'react-hook-form';
import { EHttpMethod, usePortalQuery } from '../../hooks/usePortalQuery';
import { Address, EMpanAssignmentMode, EMpanEditMode, Invoice, isSearchMeterPointDetails, MeterPointDetails, SiteContact } from '../../types';
import { FormTabHeadings } from '@portal_components/components/FormTabHeadings';
import React, { useEffect, useState } from 'react';
import { FormButton } from '@portal_components/components/FormButton';
import { yupResolver } from '@hookform/resolvers/yup';
import { SiteAddressDetailsTab } from './SiteAddressDetailsTab';
import { SiteContactDetailsTab } from './SiteContactDetailsTab';
import { SiteDetailsTab } from './SiteDetailsTab';
import { SiteInvoiceDetailsTab } from './SiteInvoiceDetailsTab';
import { JobHistoryTab } from './JobHistoryTab';
import { FormErrorList } from '@portal_components/components/FormErrorList';
import axios from 'axios';
import { EnvironmentVariables } from '../../EnvironmentVariables';
import { DateTime } from 'luxon';
import { TransferDetails } from './TransferDetails';
import { useMeterPointDetailsFormValidationSchema } from '../../hooks/useValidationSchema';

export interface AssignMpanFormProps<T extends MeterPointDetails = MeterPointDetails> {
  mpan: string;
  mode: EMpanAssignmentMode;
  editMode?: EMpanEditMode;
  address?: Address;
  contact?: SiteContact;
  existingMeterPointDetails?: T;
  customerGroupId?: string;
  contractId?: string;
  contractTypeCode?: string;
  effectiveDate?: string;
  contractStartDate?: DateTime;
  handoverFileName?: string;
  hasActiveAppointment?: boolean;
  onSubmit?: () => void | Promise<void>;
}

enum EMeterPointDetailsTab {
  SITE_DETAILS = 'Site Details',
  ADDRESS = 'Address',
  CONTACTS = 'Contact Details',
  INVOICE = 'Invoice',
  JOB_HISTORY = 'Job History'
}

export const MeterPointDetailsForm = <T extends MeterPointDetails = MeterPointDetails>({
  mpan,
  address,
  contact,
  mode,
  editMode,
  existingMeterPointDetails,
  customerGroupId,
  contractId,
  contractTypeCode,
  effectiveDate,
  contractStartDate,
  handoverFileName,
  hasActiveAppointment,
  onSubmit
}: AssignMpanFormProps<T>) => {
  const readonly = mode === EMpanAssignmentMode.VIEW;

  const validationSchema = useMeterPointDetailsFormValidationSchema(mode, contractStartDate, editMode);

  const [formSubmitted, setFormSubmitted] = useState<boolean>(false);
  const [submitResponse, setSubmitResponse] = React.useState<{ success: boolean; message: string } | null>(null);
  const [invoicesList, setInvoicesList] = useState<{ title: string; value: string }[]>([]);
  const [renderForm, setRenderForm] = useState<boolean>(editMode !== EMpanEditMode.TRANSFER);

  useEffect(() => {
    // If this is the first render, save the original value
    if (editMode === EMpanEditMode.EDIT || editMode === EMpanEditMode.TERMINATE) {
      methods.setValue('contractSiteStatusId', existingMeterPointDetails?.contractSiteStatusId);
      setRenderForm(true);
    } else if (editMode === EMpanEditMode.TRANSFER) {
      methods.setValue('transferCustomerGroupId', undefined);
      methods.setValue('transferContractId', undefined);
      methods.setValue('transferDate', undefined);
      setRenderForm(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode]);

  const [currentTab, setCurrentTab] = React.useState<EMeterPointDetailsTab>(EMeterPointDetailsTab.SITE_DETAILS);

  const handleTabSelected = (tab: EMeterPointDetailsTab) => () => {
    setCurrentTab(tab);
  };

  const methods = useForm<MeterPointDetails>({
    values: {
      customerGroupId,
      contractId,
      mprn: mpan,
      contractSiteStatusId: hasActiveAppointment ? '10' : '5',
      utilityId: contractTypeCode,
      isImport: 'Y',
      isExport: 'N',
      effectiveDate,
      address,
      contact,
      ...existingMeterPointDetails
    },
    resolver: yupResolver<MeterPointDetails>(validationSchema as any)
  });

  const { isLoading: invoicesLoading } = usePortalQuery<Invoice[]>(
    EHttpMethod.GET,
    `invoices/${customerGroupId}`,
    `invoices/${customerGroupId}`,
    !!customerGroupId && editMode !== EMpanEditMode.TRANSFER,
    undefined,
    (invoiceList: Invoice[]) => {
      setInvoicesList(
        invoiceList.map(invoice => ({
          title: `${invoice.customerInvoiceName} ${invoice.defaultInvoiceGroup ? '(Default)' : ''} ${invoice.contractId ? `(Contract ID: ${invoice.contractId})` : ''}`,
          value: invoice.invoiceGroupId
        }))
      );

      //for search and edit a default value should not be set as it may not be present on the existingMeterPointDetails.
      if (mode === EMpanAssignmentMode.VIEW || mode === EMpanAssignmentMode.EDIT) {
        if (existingMeterPointDetails?.invoiceGroupId) {
          const existingInvoice = invoiceList.find(invoice => invoice.invoiceGroupId == existingMeterPointDetails?.invoiceGroupId);
          if (existingInvoice) {
            methods.setValue('invoiceGroupId', existingInvoice?.invoiceGroupId);
            return;
          }
          if (mode !== EMpanAssignmentMode.VIEW) {
            methods.setValue('invoiceGroupId', undefined);
            return;
          }
          return;
        } else {
          return;
        }
      }

      if (mode === EMpanAssignmentMode.NEW) {
        const defaultInvoice = invoiceList.find(invoice => invoice.defaultInvoiceGroup);
        if (defaultInvoice) {
          methods.setValue('invoiceGroupId', defaultInvoice.invoiceGroupId);
          return;
        }
        const invoiceForContract = invoiceList.find(invoice => invoice.contractId === contractId);
        if (invoiceForContract) {
          methods.setValue('invoiceGroupId', invoiceForContract.invoiceGroupId);
        }
      }
    },
    undefined,
    undefined
  );

  const handleSubmit = async (values: MeterPointDetails) => {
    if (mode === EMpanAssignmentMode.VIEW) return;
    setFormSubmitted(true);
    setSubmitResponse(null);
    const fileNameQueryParam = handoverFileName ? `?handoverFileName=${encodeURIComponent(handoverFileName)}` : '';
    const callback = mode === EMpanAssignmentMode.NEW ? axios.post : axios.put;
    let path = 'assign-mpan';
    switch (editMode) {
      case EMpanEditMode.TERMINATE:
        path = 'terminate-site';
        break;
      case EMpanEditMode.TRANSFER:
        path = 'transfer-site';
        break;
      default:
        break;
    }
    const response = await callback(`${EnvironmentVariables.ApiUrl}/${path}${fileNameQueryParam}`, values, {
      headers: {
        'Content-Type': 'application/json'
      },
      withCredentials: true
    }).catch(error => error.response);
    if (!response) {
      setSubmitResponse({ success: false, message: 'An error occurred while assigning MPAN.' });
      return;
    }
    if (response.status !== 200) {
      setSubmitResponse({ success: false, message: response.data });
      setFormSubmitted(false);
      return;
    }
    // resets outer form in the MPAN assignment page
    if (onSubmit) {
      let responseMessage = `Successfully ${mode === EMpanAssignmentMode.NEW ? 'created' : 'edited'} site details`;
      switch (editMode) {
        case EMpanEditMode.TERMINATE:
          responseMessage = 'Successfully terminated site';
          break;
        case EMpanEditMode.TRANSFER:
          responseMessage = 'Successfully transferred site';
          break;
        default:
          break;
      }
      setSubmitResponse({
        success: true,
        message: responseMessage
      });
      await new Promise((res: any) => setTimeout(() => res(), 5000));
      await onSubmit();
    }
  };

  return (
    <div className="flex flex-col justify-start">
      <FormProvider {...methods}>
        <form onSubmit={!formSubmitted ? methods.handleSubmit(handleSubmit) : undefined}>
          {editMode === EMpanEditMode.TRANSFER && <TransferDetails handleSetInvoiceList={invoices => setInvoicesList(invoices)} handleSetRenderForm={(render: boolean) => setRenderForm(render)} />}
          {renderForm && (
            <>
              <FormTabHeadings<EMeterPointDetailsTab, MeterPointDetails>
                tabs={Object.values(EMeterPointDetailsTab)}
                currentTab={currentTab}
                handleTabSelected={handleTabSelected}
                conditions={{
                  [EMeterPointDetailsTab.JOB_HISTORY]: () => isSearchMeterPointDetails(existingMeterPointDetails) && mode === EMpanAssignmentMode.VIEW
                }}
                errorKeys={{
                  [EMeterPointDetailsTab.ADDRESS]: ['address'],
                  [EMeterPointDetailsTab.CONTACTS]: ['contact']
                }}
              />
              {currentTab === EMeterPointDetailsTab.SITE_DETAILS && <SiteDetailsTab readonly={readonly} mode={mode} editMode={editMode} />}
              {currentTab === EMeterPointDetailsTab.CONTACTS && <SiteContactDetailsTab readonly={readonly} />}
              {currentTab === EMeterPointDetailsTab.ADDRESS && <SiteAddressDetailsTab readonly={readonly} />}
              {currentTab === EMeterPointDetailsTab.INVOICE && <SiteInvoiceDetailsTab invoices={invoicesList} invoicesLoading={invoicesLoading} readonly={readonly} />}
              {currentTab === EMeterPointDetailsTab.JOB_HISTORY && isSearchMeterPointDetails(existingMeterPointDetails) && mode === EMpanAssignmentMode.VIEW && <JobHistoryTab />}
              <FormErrorList />
              {submitResponse && <div className={`pb-2 text-sm ${submitResponse.success ? 'text-feedback-green' : 'text-feedback-red'}`}>{submitResponse.message}</div>}
              {mode !== EMpanAssignmentMode.VIEW && contractId && (
                <div className="pl-1">
                  <FormButton type="submit" disabled={formSubmitted} primary>
                    Submit
                  </FormButton>
                </div>
              )}
            </>
          )}
        </form>
      </FormProvider>
    </div>
  );
};
